import {
  DialogBody,
  DialogContent,
  DialogCloseTrigger,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { toaster } from '@/components/ui/toaster';
import { RichTextContent } from '@/components/ui/RichTextContent';
import { useAuth } from '@/contexts/auth-context';
import { useAcceptLegalAction } from '@/hooks/actions/use-accept-legal-action';
import { usePayload } from '@/hooks/queries/use-payload';
import { useSuccessFeeTermsQuery } from '@/hooks/queries/use-success-fee-terms';
import { getLegalContentExternalUrl } from '@/lib/payload';
import { Button, HStack, Spinner, Tabs, Text, VStack } from '@chakra-ui/react';
import { currentConsentVersion } from '@fluenceur/types';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEGAL_DOCUMENT_LABELS,
  pendingLegalDocuments,
  type PendingLegalDocument,
} from './pending-legal-documents';
import { parseFrozenTerms } from './success-fee-terms/parse-frozen-terms';

export interface LegalAcceptanceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Reads the legal documents and records an explicit, voluntary acceptance. */
export function LegalAcceptanceModal({ isOpen, onClose }: LegalAcceptanceModalProps) {
  const { t } = useTranslation();
  const { user, refreshCurrentUser } = useAuth();
  const acceptLegalMutation = useAcceptLegalAction();
  const pending = pendingLegalDocuments(user);
  const [activeTab, setActiveTab] = useState<string>(pending[0] ?? 'cgu');

  // Use the centralized Payload hook for legal content
  const { useLegalContent } = usePayload();
  const {
    data: termsContent,
    isLoading: isLoadingTerms,
    error: termsError,
  } = useLegalContent('terms-of-use', { enabled: isOpen && pending.includes('cgu') });
  const {
    data: privacyContent,
    isLoading: isLoadingPrivacy,
    error: privacyError,
  } = useLegalContent('privacy-policy', { enabled: isOpen && pending.includes('privacy') });
  const cgvQuery = useSuccessFeeTermsQuery({ enabled: isOpen && pending.includes('cgv') });
  const cgvContent = parseFrozenTerms(cgvQuery.data?.document?.content);

  const documents = {
    cgu: { content: termsContent?.content, loading: isLoadingTerms, error: termsError },
    privacy: { content: privacyContent?.content, loading: isLoadingPrivacy, error: privacyError },
    cgv: { content: cgvContent?.content, loading: cgvQuery.isLoading, error: cgvQuery.error },
  };
  const isLoading = pending.some((type) => documents[type].loading);
  const hasError = pending.some((type) => !!documents[type].error);
  const canAccept =
    pending.length > 0 &&
    !isLoading &&
    !hasError &&
    pending.every((type) => !!documents[type].content) &&
    (!pending.includes('cgv') || cgvQuery.data?.document?.version === currentConsentVersion('cgv'));
  const documentNames = pending.map((type) => t(LEGAL_DOCUMENT_LABELS[type])).join(' · ');

  // Handle accepting legal documents
  const handleAccept = useCallback(async () => {
    if (!canAccept) return;
    try {
      await acceptLegalMutation.mutateAsync({
        legalAccepted: true,
        documents: pending.map((type) => ({ type, version: currentConsentVersion(type) })),
      });

      toaster.success({
        title: t('legal.acceptance.success'),
        description: t('legal.acceptance.successDescription'),
      });

      // Refresh user to update requiresLegalAcceptance flag
      await refreshCurrentUser();

      onClose();
    } catch (error) {
      console.error('Failed to accept legal documents:', error);
    }
  }, [acceptLegalMutation, refreshCurrentUser, onClose, t, canAccept, pending]);

  // Handle opening external link as fallback
  const handleOpenExternal = (type: PendingLegalDocument) => {
    const slug =
      type === 'cgu' ? 'terms-of-use' : type === 'cgv' ? 'terms-and-conditions' : 'privacy-policy';
    const externalUrl = getLegalContentExternalUrl(slug);
    window.open(externalUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => {
        if (!e.open) onClose();
      }}
      size="xl"
    >
      <DialogContent maxW={{ base: '100vw', md: '900px' }} maxH="90vh">
        <DialogCloseTrigger aria-label={t('common.close')} />
        <DialogHeader pr={12}>
          <DialogTitle>{t('legal.acceptance.title', { documents: documentNames })}</DialogTitle>
        </DialogHeader>

        <DialogBody overflowY="auto" maxH="calc(90vh - 200px)">
          <Text mb={4} color="gray.600">
            {t('legal.acceptance.description')}
          </Text>

          {isLoading && (
            <VStack py={8}>
              <Spinner size="lg" />
              <Text>{t('legal.modal.loading')}</Text>
            </VStack>
          )}

          {hasError && !isLoading && (
            <VStack py={8} gap={4}>
              <Text color="red.500">{t('legal.modal.error')}</Text>
              <HStack gap={2}>
                {pending.map((type) => (
                  <Button
                    key={type}
                    onClick={() => handleOpenExternal(type)}
                    variant="outline"
                    size="sm"
                  >
                    {t(LEGAL_DOCUMENT_LABELS[type])}
                  </Button>
                ))}
              </HStack>
            </VStack>
          )}

          {!isLoading && !hasError && pending.length > 0 && (
            <Tabs.Root value={activeTab} onValueChange={(e) => setActiveTab(e.value)}>
              <Tabs.List>
                {pending.map((type) => (
                  <Tabs.Trigger key={type} value={type}>
                    {t(LEGAL_DOCUMENT_LABELS[type])}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>

              {pending.map((type) => {
                const content = documents[type].content;
                return (
                  <Tabs.Content key={type} value={type} pt={4}>
                    {content ? (
                      <RichTextContent data={content} />
                    ) : (
                      <Text color="gray.500">{t('legal.modal.contentUnavailable')}</Text>
                    )}
                  </Tabs.Content>
                );
              })}
            </Tabs.Root>
          )}
        </DialogBody>

        <DialogFooter>
          <HStack gap={3}>
            <Button onClick={onClose} variant="ghost">
              {t('legal.acceptance.later')}
            </Button>
            <Button
              onClick={handleAccept}
              colorPalette="primary"
              loading={acceptLegalMutation.isPending}
              loadingText={t('legal.acceptance.accepting')}
              disabled={!canAccept}
            >
              {t('legal.acceptance.accept')}
            </Button>
          </HStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
